import {Divider} from "@/components/divider/Divider.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {timezoneAbbr} from "@/lib/format.tsx";
import {IncidentType, StatusPageTimeFormat} from "@/lib/types/statuspages.ts";
import classNames from "classnames";
import {formatInTimeZone} from "date-fns-tz";
import {forwardRef} from "react";
import type {IconType} from "react-icons";
import {FiActivity, FiAlertTriangle, FiInfo, FiTool, FiTrendingDown} from "react-icons/fi";
import {
  buildComponentStatusRank,
  getAffectedComponentsInPeriod,
  getIncidentsInPeriod,
} from "../../utils/uptimeBarEvents.ts";
import {IncidentListSection} from "./IncidentListSection.tsx";
import styles from "./StatusPageUptimeBarTooltip.module.scss";
import type {StatusPageUptimeBarTooltipProps} from "./StatusPageUptimeBarTooltip.types.ts";

const MAX_LIST_LENGTH = 3;

const STATUS_ICON: Record<string, IconType> = {
  "major-outage": FiAlertTriangle,
  "partial-outage": FiAlertTriangle,
  "degraded-performance": FiTrendingDown,
  notification: FiInfo,
  "under-maintenance": FiTool,
  operational: FiActivity,
};

const formatPeriodLabel = (
  periodStart: Date,
  periodEnd: Date,
  timezone: string,
  timeFormat?: StatusPageTimeFormat
): string => {
  const timeFmt = timeFormat === StatusPageTimeFormat.TWENTY_FOUR_HOUR ? "HH:mm:ss" : "hh:mm:ss a";
  const startAbbr = timezoneAbbr(timezone, periodStart);
  const endAbbr = timezoneAbbr(timezone, periodEnd);
  // A period can straddle a DST change (e.g. EST\u2192EDT); label the start too when its abbreviation differs.
  const startAbbrSuffix = startAbbr !== endAbbr ? ` ${startAbbr}` : "";
  const startStr = `${formatInTimeZone(periodStart, timezone, `MMM d, yyyy, ${timeFmt}`)}${startAbbrSuffix}`;
  const endStr = `${formatInTimeZone(periodEnd, timezone, timeFmt)} ${endAbbr}`;
  return `${startStr} \u2013 ${endStr}`;
};

export const StatusPageUptimeBarTooltip = forwardRef<HTMLDivElement, StatusPageUptimeBarTooltipProps>(
  ({periodStart, periodEnd, incidents, statusMetadata, timezone = "UTC", timeFormat, className, ...rest}, ref) => {
    const isFuturePeriod = periodStart.getTime() >= Date.now();
    const periodIncidents = getIncidentsInPeriod(incidents, periodStart, periodEnd);
    const statusRank = buildComponentStatusRank(statusMetadata);

    if (periodIncidents.length === 0) {
      return (
        <div ref={ref} className={classNames(styles.container, className)} {...rest}>
          <div className={styles.dateLabel}>{formatPeriodLabel(periodStart, periodEnd, timezone, timeFormat)}</div>
          {isFuturePeriod && <div className={styles.noDataLabel}>No Data</div>}
        </div>
      );
    }

    const sortBySeverity = (items: typeof periodIncidents) =>
      [...items].sort((a, b) => {
        const worstA = Math.min(...(a.affected_components?.map((c) => statusRank[c.status] ?? 99) ?? [99]));
        const worstB = Math.min(...(b.affected_components?.map((c) => statusRank[c.status] ?? 99) ?? [99]));
        if (worstA !== worstB) return worstA - worstB;
        return b.duration - a.duration;
      });

    const sortedIncidents = sortBySeverity(
      periodIncidents.filter((inc) => inc.incident_type === IncidentType.INCIDENT && inc.include_in_global_metrics)
    );
    const sortedMaintenance = sortBySeverity(
      periodIncidents.filter((inc) => inc.incident_type === IncidentType.SCHEDULED_MAINTENANCE)
    );

    const affectedComponents = getAffectedComponentsInPeriod(periodIncidents, statusRank);
    const componentEntries = Object.entries(affectedComponents).sort(
      ([, a], [, b]) => (statusRank[a] ?? 99) - (statusRank[b] ?? 99)
    );

    return (
      <div ref={ref} className={classNames(styles.container, className)} {...rest}>
        <div className={styles.dateLabel}>{formatPeriodLabel(periodStart, periodEnd, timezone, timeFormat)}</div>

        <IncidentListSection title="Incidents" items={sortedIncidents} />
        <IncidentListSection title="Maintenance" items={sortedMaintenance} />

        {componentEntries.length > 0 && (
          <>
            <Divider />
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Affected Components ({componentEntries.length})</div>
              <div className={styles.componentList}>
                {componentEntries.slice(0, MAX_LIST_LENGTH).map(([name, status]) => (
                  <div key={name} className={styles.componentItem}>
                    <Icon
                      icon={STATUS_ICON[status] ?? FiActivity}
                      size="normal"
                      className={styles[status.replace(/-/g, "_")] ?? styles.statusDefault}
                    />
                    {name}
                  </div>
                ))}
                {componentEntries.length > MAX_LIST_LENGTH && (
                  <div className={styles.moreText}>+ {componentEntries.length - MAX_LIST_LENGTH} more</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
);

StatusPageUptimeBarTooltip.displayName = "StatusPageUptimeBarTooltip";
